import React, { useState, useEffect } from 'react';
import './usertools-css/VitaminCCalculator.css';
import '../../styles/components/ainutritionlandingpage.css';

const vitCRdaTable = {
    // mg/day
    '0-6m': { rda: 40, isAI: true },
    '7-12m': { rda: 50, isAI: true },
    '1-3y': { rda: 15 },
    '4-8y': { rda: 25 },
    '9-13y': { rda: 45 },
    '14-18_male': { rda: 75 },
    '14-18_female': { rda: 65 },
    '19+_male': { rda: 90 },
    '19+_female': { rda: 75 },
    'pregnant_14-18': { rda: 80 },
    'pregnant_19+': { rda: 85 },
    'lactating_14-18': { rda: 115 },
    'lactating_19+': { rda: 120 },
    'smoker_bonus': 35, // Added to base RDA
};

const vitC_UL = 2000; // Tolerable Upper Intake Level for adults in mg/day

// Simplified stress bonus (mg)
const stressBonusTable = {
    none: 0,
    moderate: 50, // Example values
    high: 200,    // Example values
};

// Simplified food intake estimates (mg/day) based on fruit/veg servings
const foodIntakeEstimates = {
    low: 30,    // e.g., 0-2 servings/day
    medium: 90,  // e.g., 3-5 servings/day
    high: 200, // e.g., 5+ servings/day
};

// Bonus added if specific rich foods are regularly consumed (mg/day)
// Applied ON TOP of the baseline estimate
const richFoodBonusAmounts = {
    none: 0,
    few: 30,  // If 1 or 2 are checked
    many: 60, // If 3 or 4 are checked
};

// --- NEW: Data for food suggestions ---
const foodSuggestionsData = [
    { name: "Red Bell Pepper (1/2 cup, raw)", mg: 95 },
    { name: "Orange (1 medium)", mg: 70 },
    { name: "Kiwi (1 medium)", mg: 64 },
    { name: "Broccoli (1/2 cup, cooked)", mg: 51 },
    { name: "Strawberries (1/2 cup, sliced)", mg: 49 },
    { name: "Brussels Sprouts (1/2 cup, cooked)", mg: 48 },
    { name: "Grapefruit (1/2 medium)", mg: 39 },
    // Add more if desired
];
// --------------------------------------

// --- Component ---
function VitaminCCalculator() {
    // --- State for User Inputs ---
    const [age, setAge] = useState('');
    const [sex, setSex] = useState(''); // 'male', 'female'
    const [isPregnant, setIsPregnant] = useState(false);
    const [isLactating, setIsLactating] = useState(false);
    const [isSmoker, setIsSmoker] = useState(false);
    const [stressLevel, setStressLevel] = useState('none'); // 'none', 'moderate', 'high'
    const [fruitVegIntakeLevel, setFruitVegIntakeLevel] = useState(''); // 'low', 'medium', 'high'

    // NEW: State for specific Vitamin C rich food consumption
    const [richFoodSources, setRichFoodSources] = useState({
        citrus: false,
        bellPeppers: false,
        broccoli: false,
        strawberries: false,
    });

    // Health Factors State (Vitamin C relevant)
    const [healthFactors, setHealthFactors] = useState({
        cond_gi_malabsorption: false,
        cond_kidney_stones: false,
        cond_g6pd_deficiency: false,
        // Add others if needed e.g., wound healing, critical illness
    });

    // Supplement State
    const [takesSupplementC, setTakesSupplementC] = useState(false);
    const [supplementDoseMg, setSupplementDoseMg] = useState(''); // Dose in mg
    const [supplementFrequencyC, setSupplementFrequencyC] = useState(''); // 'daily', 'weekly', etc.

    // --- State for Results ---
    const [calculationResult, setCalculationResult] = useState(null); // Will hold results object
    const [showResults, setShowResults] = useState(false);

    // --- Input Handlers ---
    const handleHealthFactorChange = (event) => {
        const { name, checked } = event.target;
        setHealthFactors(prev => ({ ...prev, [name]: checked }));
    };

    // NEW: Handler for rich food checkboxes
    const handleRichFoodChange = (event) => {
        const { name, checked } = event.target;
        setRichFoodSources(prev => ({ ...prev, [name]: checked }));
    };

    // --- Derived State ---
    const ageNum = parseInt(age, 10);

    // Disable pregnancy/lactation based on sex and age
    useEffect(() => {
        if (sex === 'male' || isNaN(ageNum) || ageNum < 14 || ageNum > 55) { // Added isNaN check
            setIsPregnant(false);
            setIsLactating(false);
        }
    }, [sex, ageNum]);

    // --- Calculation Logic ---
    const calculateVitaminCIntake = (e) => {
        e.preventDefault();
        setShowResults(false);
        setCalculationResult(null);

        // Initialize calculation variables
        let explanation = [];
        let safetyFlags = [];
        let targetRda = 0;
        let recommendations = [];
        let detailedFoodSuggestionsHTML = ''; // To store the tailored food text/HTML

        // 1. Determine Base RDA based on Age/Sex/Pregnancy/Lactation
        let baseRdaKey = '';
        if (!isNaN(ageNum)) {
            if (isLactating) {
                baseRdaKey = ageNum >= 19 ? 'lactating_19+' : 'lactating_14-18';
            } else if (isPregnant) {
                baseRdaKey = ageNum >= 19 ? 'pregnant_19+' : 'pregnant_14-18';
            } else if (ageNum >= 0 && ageNum < 0.5) {
                baseRdaKey = '0-6m';
            } else if (ageNum >= 0.5 && ageNum < 1) {
                baseRdaKey = '7-12m';
            } else if (ageNum >= 1 && ageNum <= 3) {
                baseRdaKey = '1-3y';
            } else if (ageNum >= 4 && ageNum <= 8) {
                baseRdaKey = '4-8y';
            } else if (ageNum >= 9 && ageNum <= 13) {
                baseRdaKey = '9-13y';
            } else if (ageNum >= 14 && ageNum <= 18) {
                baseRdaKey = sex === 'male' ? '14-18_male' : '14-18_female';
            } else if (ageNum >= 19) {
                baseRdaKey = sex === 'male' ? '19+_male' : '19+_female';
            }

            if (vitCRdaTable[baseRdaKey]) {
                targetRda = vitCRdaTable[baseRdaKey].rda;
                explanation.push(`Base ${vitCRdaTable[baseRdaKey].isAI ? 'AI' : 'RDA'} (${baseRdaKey.replace(/_/g, ' ').replace('male', 'M').replace('female', 'F')}): ${targetRda} mg/day.`);
            } else {
                explanation.push("Could not determine base RDA for the inputs provided.");
                targetRda = sex === 'male' ? vitCRdaTable['19+_male'].rda : vitCRdaTable['19+_female'].rda; // Fallback
            }

            // Add Smoker Bonus
            if (isSmoker) {
                const smokerBonus = vitCRdaTable['smoker_bonus'];
                targetRda += smokerBonus;
                explanation.push(`+${smokerBonus} mg/day added for smoking status.`);
            }

            // Add Stress Bonus
            const stressBonus = stressBonusTable[stressLevel] || 0;
            if (stressBonus > 0) {
                targetRda += stressBonus;
                explanation.push(`+${stressBonus} mg/day added for ${stressLevel} stress level.`);
            }
            explanation.push(`-> Adjusted Target Intake: ${targetRda} mg/day.`);

        } else {
            explanation.push("Please enter a valid age.");
            targetRda = vitCRdaTable['19+_male'].rda; // Default fallback
        }


        // 2. Estimate Food Intake (Simplified + Rich Food Bonus)
        let baselineFoodIntake = foodIntakeEstimates[fruitVegIntakeLevel] || 0;
        let foodIntake = baselineFoodIntake; // Start with baseline

        if (fruitVegIntakeLevel) {
            explanation.push(`Estimated baseline food intake (${fruitVegIntakeLevel} fruit/veg): ~${baselineFoodIntake} mg/day.`);
        } else {
            explanation.push("Select general fruit/vegetable intake level to estimate baseline food contribution.");
        }

        // --- Apply Rich Food Bonus ---
        const checkedRichFoodsCount = Object.values(richFoodSources).filter(Boolean).length;
        let richFoodBonus = 0;
        if (checkedRichFoodsCount > 0 && checkedRichFoodsCount <= 2) {
            richFoodBonus = richFoodBonusAmounts.few;
        } else if (checkedRichFoodsCount >= 3) {
            richFoodBonus = richFoodBonusAmounts.many;
        }

        if (richFoodBonus > 0) {
            foodIntake += richFoodBonus;
            explanation.push(`+${richFoodBonus} mg/day bonus added for regular consumption of ${checkedRichFoodsCount} specific high-Vitamin C food(s).`);
        }
        // ------------------------------------

        explanation.push(`-> Total Estimated Dietary Intake: ~${foodIntake} mg/day.`);
        // Add fortified food calculation here if input is added later

        // 3. Calculate Supplement Intake
        let supplementIntake = 0;
        const dose = parseFloat(supplementDoseMg);
        if (takesSupplementC && !isNaN(dose) && dose > 0 && supplementFrequencyC) {
            let dailyFactor = 0;
            switch (supplementFrequencyC) {
                case 'daily': dailyFactor = 1; break;
                case '2x_day': dailyFactor = 2; break;
                case '3x_week': dailyFactor = 3 / 7; break;
                case 'weekly': dailyFactor = 1 / 7; break;
                case 'as_needed': dailyFactor = 0.1; break; // Very rough estimate for occasional use
                default: dailyFactor = 0; break;
            }
            supplementIntake = dose * dailyFactor;
            if (supplementFrequencyC === 'as_needed' && dailyFactor > 0) {
                explanation.push(`Supplement contribution (estimated low due to 'as needed' frequency): ${dose} mg * occasional factor ≈ ${supplementIntake.toFixed(0)} mg/day average.`);
            } else if (dailyFactor > 0) {
                explanation.push(`Supplement contribution: ${dose} mg taken ${supplementFrequencyC.replace('_', ' ')} ≈ ${supplementIntake.toFixed(0)} mg/day average.`);
            } else if (supplementFrequencyC && dailyFactor === 0 && supplementFrequencyC !== 'as_needed') {
                explanation.push(`Supplement frequency selected (${supplementFrequencyC.replace('_', ' ')}) is not accounted for in daily average calculation.`);
            } else if (!supplementFrequencyC) {
                explanation.push(`Supplement frequency not selected.`);
            }
        }

        const rawIntake = foodIntake + supplementIntake;
        explanation.push(`Estimated total raw intake (Food + Supplements): ${rawIntake.toFixed(0)} mg/day.`);

        // 4. Calculate Absorption Adjustment (Simplified)
        let absorptionFactor = 1.0;
        if (healthFactors.cond_gi_malabsorption) {
            absorptionFactor = 0.8; // Example: 20% reduction
            explanation.push(`Absorption potentially reduced by ~20% due to GI malabsorption factor.`);
            safetyFlags.push({ type: 'info', text: 'GI malabsorption selected; actual absorption may vary.' });
        }
        // Only add this explanation line if factor is not 100%
        if (absorptionFactor !== 1.0) {
            explanation.push(`Absorption factor applied: ${(absorptionFactor * 100).toFixed(0)}%.`);
        }


        // 5. Calculate Effective Intake
        const effectiveIntake = rawIntake * absorptionFactor;
        explanation.push(`Estimated effective intake (after absorption adjustment): ${effectiveIntake.toFixed(0)} mg/day.`);

        // 6. Calculate Gap
        const gap = targetRda - effectiveIntake;
        const absGap = Math.abs(gap).toFixed(0); // Absolute value for display
        explanation.push(`Gap compared to Target: ${gap > 0 ? `${gap.toFixed(0)} mg/day deficit` : `${absGap} mg/day surplus (or adequate)`}.`);

        // 7. Safety Checks
        const totalDailyIntakeForUL = rawIntake; // Use raw intake for UL check
        if (totalDailyIntakeForUL > vitC_UL) {
            safetyFlags.push({ type: 'warning', text: `Total daily intake (${totalDailyIntakeForUL.toFixed(0)} mg) exceeds the Tolerable Upper Intake Level (UL) of ${vitC_UL} mg/day. High doses may cause GI distress or increase kidney stone risk in susceptible individuals.` });
        }
        if (healthFactors.cond_kidney_stones && supplementIntake > 250) {
            safetyFlags.push({ type: 'warning', text: `History of kidney stones selected. Vitamin C supplementation (especially >${supplementIntake > 500 ? '500' : '250'}mg/day) may increase risk of oxalate stones. Discuss with your doctor.` });
        }
        if (healthFactors.cond_g6pd_deficiency && supplementIntake > 500) {
            safetyFlags.push({ type: 'severe', text: 'G6PD deficiency selected. High doses of Vitamin C (especially IV) can pose a risk of hemolysis. Discuss any supplementation >500mg/day with your doctor.' });
        }
        if (effectiveIntake < 10 && targetRda > 10) {
            safetyFlags.push({ type: 'severe', text: 'Estimated intake is critically low (<10 mg/day). Significant risk of deficiency (scurvy). Seek medical advice urgently.' });
        }


        // 8. Generate Recommendations AND Tailored Food Suggestions
        if (gap > 0) {
            recommendations.push(`Your estimated intake is about ${gap.toFixed(0)} mg below your target.`);
            recommendations.push("Focus on increasing Vitamin C-rich foods: citrus fruits, berries (strawberries, kiwi), bell peppers, broccoli, tomatoes, cantaloupe.");

            // --- Generate Detailed Food Suggestion Text (Deficit) ---
            detailedFoodSuggestionsHTML = `
                <p><strong>To help close the ~${gap.toFixed(0)} mg gap, consider incorporating more Vitamin C-rich foods.</strong> Here are some examples and their approximate Vitamin C content per serving:</p>
                <ul>`;
            // Show a few relevant examples
            const suggestionsToShow = foodSuggestionsData.filter(food => food.mg > 20).slice(0, 5); // Show up to 5 good sources
            suggestionsToShow.forEach(food => {
                detailedFoodSuggestionsHTML += `<li>${food.name} ≈ <strong>${food.mg}mg</strong></li>`;
            });
            detailedFoodSuggestionsHTML += `</ul>`;
            if (gap <= 75) {
                detailedFoodSuggestionsHTML += `<p>Adding just <strong>one serving</strong> of foods like oranges, kiwi, or bell peppers could significantly help meet your target.</p>`;
            } else {
                detailedFoodSuggestionsHTML += `<p>Adding <strong>one or two servings</strong> of these foods daily can significantly boost your intake towards your target.</p>`;
            }
            // --- Add supplement link recommendation (Tailored) ---
            detailedFoodSuggestionsHTML += `
                <p>
                    <i>If dietary changes are insufficient, you might explore <a href="https://purehealthnexus.com/top-rated-vitamins" target="_blank" rel="noopener noreferrer">Top Rated Vitamin C Supplements</a> for additional support, keeping the safety alerts above in mind.</i>
                </p>`;
            // ---------------------------------------------------------

            // Add supplement recommendations based on gap size
            if (richFoodBonus === 0 && gap > richFoodBonusAmounts.few) {
                recommendations.push("Consider regularly including 1-2 specific high-Vitamin C foods like citrus, bell peppers, strawberries, or broccoli.");
            }
            if (gap >= 100 && !takesSupplementC) {
                recommendations.push("Consider a low-to-moderate dose supplement (e.g., 250-500 mg/day) if dietary changes are insufficient or difficult. Choose Ascorbic Acid or buffered forms.");
            } else if (takesSupplementC && gap > 50) {
                recommendations.push("Review your current supplement dose/frequency or dietary intake to close the remaining gap.");
            } else if (gap > 0 && !takesSupplementC && gap < 100) {
                recommendations.push("Focus on dietary improvements first. A small supplement (e.g., 100-250mg) could be considered if needed.")
            }

        } else { // Intake is adequate or surplus
            recommendations.push(`Your estimated intake appears adequate or above your target (${absGap} mg surplus) based on inputs.`);

            // --- Generate Detailed Food Suggestion Text (Adequate/Surplus) ---
            detailedFoodSuggestionsHTML = `
                <p><strong>Your intake appears adequate.</strong> Continuing to include a variety of Vitamin C-rich foods helps maintain your levels and provides other beneficial nutrients. Good sources include:</p>
                <ul>`;
            // Show a few examples
            const suggestionsToShow = foodSuggestionsData.slice(0, 4); // Show 4 examples
            suggestionsToShow.forEach(food => {
                detailedFoodSuggestionsHTML += `<li>${food.name} ≈ ${food.mg}mg</li>`;
            });
            detailedFoodSuggestionsHTML += `</ul>`;
            // --- Add supplement link placeholder (Adequate) ---
            detailedFoodSuggestionsHTML += `
                <p>
                    <i>If you choose to supplement, ensure it's necessary and consider the safety alerts above regarding the Upper Limit. You can review options like <a href="https://purehealthnexus.com/top-rated-vitamins" target="_blank" rel="noopener noreferrer">Top Rated Vitamin C Supplements</a>.</i>
                </p>`;
            // ---------------------------------------------------------

            if (richFoodBonus > 0) {
                recommendations.push("Continuing to include the selected high-Vitamin C foods is beneficial for maintaining intake and obtaining other nutrients.");
            }
            if (totalDailyIntakeForUL > vitC_UL * 0.75 && totalDailyIntakeForUL <= vitC_UL) { // Nearing UL
                recommendations.push("Your intake is approaching the Upper Limit (UL). Ensure high supplemental doses are necessary and medically advised.");
            }
        }

        // Add common recommendations
        if (isSmoker) {
            recommendations.push("As a smoker, consistently meeting the higher Vitamin C target is important to counteract oxidative stress.");
        }
        recommendations.push("Remember, whole foods provide other beneficial nutrients alongside Vitamin C.");
        recommendations.push("Consult your healthcare provider to discuss personalized needs, especially if managing health conditions or considering high doses.");


        // Set results after a short delay
        setTimeout(() => {
            setCalculationResult({
                rda: targetRda,
                rawIntake: rawIntake.toFixed(0),
                foodIntake: foodIntake.toFixed(0),
                supplementIntake: supplementIntake.toFixed(0),
                absorptionFactor: (absorptionFactor * 100).toFixed(0),
                effectiveIntake: effectiveIntake.toFixed(0),
                gap: gap.toFixed(0), // Keep the signed gap
                explanation: explanation.join('\n'),
                recommendations: recommendations,
                safetyFlags: safetyFlags,
                detailedFoodSuggestionsHTML: detailedFoodSuggestionsHTML // STORE the new HTML string
            });
            setShowResults(true);
        }, 300);
    };

    // --- JSX Structure ---
    return (
        <div className="vitaminCCalculator">
            <h1>
                <span>
                    <span role="img" aria-label="vitamin C calculator">
                        🍊
                    </span>{' '}
                    Vitamin C Intake Calculator
                </span>

                <span className="ai-promo-inline">
                    <strong>Unlock Smarter Eating:</strong>{' '}
                    Get a complete breakdown of vitamins, minerals, and fats plus a personalized
                    weight-loss plan or nutrition tailored to your training with a single{' '}
                    <a
                        href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals"
                        className="ai-promo-link"
                    >
                        AI scan
                    </a>.
                </span>
            </h1>

            <p className="important-note">
                <strong>Disclaimer:</strong> This tool provides an ESTIMATE based on general guidelines and simplified inputs. It is NOT a substitute for professional medical advice, diagnosis, or treatment. Food intake estimations are very basic (using general servings and specific food bonuses). Absorption varies. Always consult your physician or qualified health provider regarding your Vitamin C needs, especially if you smoke, have health conditions, are pregnant/lactating, or considering high-dose supplements. Do not exceed 2000 mg/day without medical advice due to potential side effects.
            </p>

            {/* --- Input Form --- */}
            <form onSubmit={calculateVitaminCIntake}>
                <h2>🧾 Your Information</h2>

                {/* Demographics */}
                <div className="form-section">
                    <label>
                        Age (Years):
                        <input type="number" value={age} onChange={(e) => setAge(e.target.value)} required min="0" max="120" placeholder="e.g., 35" />
                    </label>
                    <label>
                        Sex Assigned at Birth:
                        <select value={sex} onChange={(e) => setSex(e.target.value)} required>
                            <option value="">-- Select --</option>
                            <option value="female">Female</option>
                            <option value="male">Male</option>
                        </select>
                    </label>
                    {sex === 'female' && !isNaN(ageNum) && ageNum >= 14 && ageNum <= 55 && (
                        <>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={isPregnant} onChange={(e) => setIsPregnant(e.target.checked)} disabled={isLactating} />
                                Currently Pregnant
                            </label>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={isLactating} onChange={(e) => setIsLactating(e.target.checked)} disabled={isPregnant} />
                                Currently Lactating
                            </label>
                        </>
                    )}
                </div>

                {/* Lifestyle Factors */}
                <div className="form-section">
                    <h3>🚬 Lifestyle Factors</h3>
                    <label className="checkbox-label">
                        <input type="checkbox" checked={isSmoker} onChange={(e) => setIsSmoker(e.target.checked)} />
                        Are you currently a smoker or regularly exposed to significant second-hand smoke? (+35 mg/day need)
                    </label>
                    <label>
                        Current Stress Level:
                        <select value={stressLevel} onChange={(e) => setStressLevel(e.target.value)}>
                            <option value="none">None / Low</option>
                            <option value="moderate">Moderate (e.g., illness, heavy training, high pressure)</option>
                            <option value="high">High / Severe (e.g., surgery, critical illness, extreme stress)</option>
                        </select>
                        <small>Significantly increases Vitamin C needs.</small>
                    </label>
                </div>

                {/* Diet (Simplified + Rich Food Selection) */}
                <div className="form-section">
                    <h3>🍓 Dietary Habits (Estimate)</h3>
                    <label>
                        Typical Daily Fruit & Vegetable Intake (Overall):
                        <select value={fruitVegIntakeLevel} onChange={(e) => setFruitVegIntakeLevel(e.target.value)} required>
                            <option value="">-- Select --</option>
                            <option value="low">Low (0-2 servings)</option>
                            <option value="medium">Medium (3-5 servings)</option>
                            <option value="high">High (5+ servings)</option>
                        </select>
                        <small>1 serving ≈ 1 medium fruit, 1/2 cup veg/berries. Provides a baseline estimate.</small>
                    </label>

                    {/* --- Rich Food Checkboxes --- */}
                    <fieldset className="rich-food-fieldset">
                        <legend>Do you REGULARLY consume any of these high-Vitamin C foods?</legend>
                        <small>Checking these refines the dietary estimate upwards.</small>
                        <div className="checkbox-group">
                            <label className="checkbox-label indented">
                                <input type="checkbox" name="citrus" checked={richFoodSources.citrus} onChange={handleRichFoodChange} />
                                Citrus Fruits (Oranges, Grapefruit, Lemon, Lime)
                            </label>
                            <label className="checkbox-label indented">
                                <input type="checkbox" name="bellPeppers" checked={richFoodSources.bellPeppers} onChange={handleRichFoodChange} />
                                Bell Peppers (especially Red/Yellow/Orange)
                            </label>
                            <label className="checkbox-label indented">
                                <input type="checkbox" name="broccoli" checked={richFoodSources.broccoli} onChange={handleRichFoodChange} />
                                Broccoli
                            </label>
                            <label className="checkbox-label indented">
                                <input type="checkbox" name="strawberries" checked={richFoodSources.strawberries} onChange={handleRichFoodChange} />
                                Strawberries (or Kiwi)
                            </label>
                        </div>
                    </fieldset>
                    {/* ------------------------------------ */}
                </div>

                {/* Health Factors (Vitamin C Relevant) */}
                <div className="form-section">
                    <h3>🩺 Health Factors & Conditions</h3>
                    <fieldset>
                        <legend>Check any that apply (may affect needs or safety):</legend>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="cond_gi_malabsorption" checked={healthFactors.cond_gi_malabsorption} onChange={handleHealthFactorChange} />
                            Condition affecting nutrient absorption? (e.g., Crohn's, Celiac, Bariatric Surgery)
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="cond_kidney_stones" checked={healthFactors.cond_kidney_stones} onChange={handleHealthFactorChange} />
                            History of calcium-oxalate kidney stones? (Caution with high doses)
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="cond_g6pd_deficiency" checked={healthFactors.cond_g6pd_deficiency} onChange={handleHealthFactorChange} />
                            Known G6PD deficiency? (Risk with very high doses)
                        </label>
                    </fieldset>
                </div>

                {/* Supplement Intake */}
                <div className="form-section">
                    <h3>💊 Vitamin C Supplement Use</h3>
                    <label className="checkbox-label">
                        <input type="checkbox" checked={takesSupplementC} onChange={(e) => setTakesSupplementC(e.target.checked)} />
                        Do you currently take a Vitamin C supplement?
                    </label>
                    {takesSupplementC && (
                        <div className='supplement-details'>
                            <label>
                                Dose per intake (mg):
                                <input type="number" value={supplementDoseMg} onChange={(e) => setSupplementDoseMg(e.target.value)} required={takesSupplementC} min="1" placeholder="e.g., 500" />
                            </label>
                            <label>
                                Frequency:
                                <select value={supplementFrequencyC} onChange={(e) => setSupplementFrequencyC(e.target.value)} required={takesSupplementC}>
                                    <option value="">-- Select --</option>
                                    <option value="daily">Daily</option>
                                    <option value="2x_day">Twice a day</option>
                                    <option value="3x_week">3 times per week</option>
                                    <option value="weekly">Once per week</option>
                                    <option value="as_needed">As needed / Occasionally</option>
                                </select>
                            </label>
                        </div>
                    )}
                </div>

                <button type="submit">Calculate Vitamin C Intake</button>
            </form>

            {/* --- Results Display --- */}
            {showResults && calculationResult && (
                <div className="results-section">
                    <h2>📊 Estimated Vitamin C Status</h2>
                    {/* Safety Flags Display */}
                    {calculationResult.safetyFlags.length > 0 && (
                        <div className="safety-alerts">
                            <h4>⚠️ Safety Alerts & Considerations</h4>
                            <ul>
                                {calculationResult.safetyFlags.map((flag, index) => (
                                    <li key={index} className={`alert-${flag.type}`}>
                                        <strong>{flag.type.toUpperCase()}:</strong> {flag.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="results-grid">
                        <div className="result-item">
                            <span className="result-label">Target RDA/AI:</span>
                            <span className="result-value">{calculationResult.rda} mg/day</span>
                            <small className='result-note'>(Adjusted for age, sex, life stage, smoking, stress)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Dietary Intake:</span>
                            <span className="result-value">{calculationResult.foodIntake} mg/day</span>
                            <small className='result-note'>(Baseline from servings + bonus for rich foods)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Supplement Intake:</span>
                            <span className="result-value">{calculationResult.supplementIntake} mg/day</span>
                            <small className='result-note'>(Average daily from supplement info)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Total Raw Intake:</span>
                            <span className="result-value">{calculationResult.rawIntake} mg/day</span>
                            <small className='result-note'>(Food + Supplements, before absorption)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Absorption Factor:</span>
                            <span className="result-value">{calculationResult.absorptionFactor}%</span>
                            <small className='result-note'>(Adjusted for GI conditions)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Effective Intake:</span>
                            <span className="result-value significant">{calculationResult.effectiveIntake} mg/day</span>
                            <small className='result-note'>(Amount likely utilized; less efficient &gt;200mg doses)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Intake Status vs Target:</span>
                            <span className={`result-value status-${calculationResult.gap > 20 ? 'low' : calculationResult.gap > 0 ? 'borderline' : 'adequate'}`}>
                                {calculationResult.gap > 20 ? `Likely Low (~${calculationResult.gap} mg deficit)` : calculationResult.gap > 0 ? `Borderline (~${calculationResult.gap} mg deficit)` : 'Likely Adequate/Surplus'}
                            </span>
                        </div>
                    </div>

                    <h3>📝 Explanation & Recommendations</h3>
                    <pre className="explanation-box">{calculationResult.explanation}</pre>

                    <div className='recommendations-box'>
                        <h4>Recommendations Summary:</h4>
                        <ul>
                            {calculationResult.recommendations.map((rec, index) => (
                                <li key={index}>{rec}</li>
                            ))}
                        </ul>

                        {/* --- UPDATED SECTION for Dynamic Food Suggestions --- */}
                        <div
                            className="detailed-food-suggestions"
                            style={{ marginTop: '15px', borderTop: '1px solid #eee', paddingTop: '15px' }} // Optional styling
                            dangerouslySetInnerHTML={{ __html: calculationResult.detailedFoodSuggestionsHTML }}
                        />
                        {/* ---------------------------------------------------- */}

                        <a href="#vitC-education" className="scroll-link">Learn more about Vitamin C below</a>
                    </div>

                    <p className="important-note">
                        <strong>Crucial Reminder:</strong> This estimate is based on averages and simplified inputs. Individual needs and absorption vary. This tool CANNOT diagnose deficiency (scurvy) or replace medical advice. Consult your healthcare provider for personalized guidance, especially if symptoms are present or considering doses above the RDA.
                    </p>
                </div>
            )}

            {/* --- Educational Content (Vitamin C specific) --- */}
            <div id="vitC-education" className="educational-section">
                <h2>Understanding Vitamin C</h2>
                { /* ... Educational content remains the same ... */}
                <div className="content-block">
                    <h3>What is Vitamin C and Why is it Important?</h3>
                    <p>
                        Vitamin C (Ascorbic Acid) is a water-soluble vitamin essential for numerous biological processes. It's a powerful antioxidant, protecting cells from damage. Key roles include:
                    </p>
                    <ul>
                        <li><strong>Collagen Synthesis:</strong> Crucial for skin, tendons, ligaments, blood vessels, and wound healing.</li>
                        <li><strong>Neurotransmitter & Carnitine Synthesis:</strong> Involved in producing brain chemicals (like norepinephrine) and carnitine (energy metabolism).</li>
                        <li><strong>Antioxidant Recycling:</strong> Helps regenerate other antioxidants like Vitamin E and glutathione.</li>
                        <li><strong>Immune Function:</strong> Supports various immune cell functions.</li>
                        <li><strong>Iron Absorption:</strong> Enhances the absorption of non-heme iron (from plant sources).</li>
                    </ul>
                </div>

                <div className="content-block">
                    <h3>How is Vitamin C Absorbed?</h3>
                    <ul>
                        <li><strong>Active Transport:</strong> Absorbed in the small intestine via specific transporters (SVCT1 and SVCT2).</li>
                        <li><strong>Saturation:</strong> This transport system becomes saturated at higher intakes. Absorption efficiency is high (~70-90%) at typical dietary doses (30-180 mg/day) but drops significantly (to &lt;50%) for single doses above 1000 mg (1g). Unabsorbed Vitamin C remains in the gut and can cause osmotic effects (diarrhea).</li>
                        <li><strong>Malabsorption:</strong> GI conditions affecting the small intestine can reduce absorption efficiency.</li>
                        <li><strong>Excretion:</strong> Excess Vitamin C that isn't absorbed or stored is excreted in the urine, usually within hours.</li>
                    </ul>
                </div>

                <div className="content-block">
                    <h3>Recommended Intakes (RDA) & Sources</h3>
                    <ul>
                        <li><strong>RDA:</strong> Varies by age, sex, pregnancy, and lactation status (see calculator target). Adults typically need 75 mg (Females) to 90 mg (Males) daily.</li>
                        <li><strong>Smokers:</strong> Require an additional 35 mg/day due to increased oxidative stress depleting Vitamin C stores.</li>
                        <li><strong>Rich Food Sources:</strong> Fruits and vegetables are the best sources. Excellent sources include citrus fruits (oranges, grapefruit), kiwi, strawberries, bell peppers (especially red), broccoli, tomatoes, cantaloupe, Brussels sprouts, and potatoes.</li>
                        <li><strong>Fortified Foods:</strong> Some juices, cereals, and "immunity" drinks are fortified with Vitamin C (check labels).</li>
                        <li><strong>Supplements:</strong> Commonly available as Ascorbic Acid. Buffered forms (e.g., Calcium Ascorbate, Sodium Ascorbate, Ester-C®) may be gentler on the stomach. Liposomal forms claim enhanced absorption but need more robust evidence. Doses range widely (e.g., 60mg, 250mg, 500mg, 1000mg+).</li>
                        <li><strong>Body Stores:</strong> The body holds around 1.5 grams (1500 mg) when fully saturated, mainly in cells and tissues. Deficiency symptoms appear when stores drop significantly (below ~300mg).</li>
                    </ul>
                </div>

                <div className="content-block">
                    <h3>Who is at Risk for Inadequacy or Needs More?</h3>
                    <ul>
                        <li><strong>Smokers & Passive Smokers:</strong> Due to higher oxidative stress.</li>
                        <li><strong>Individuals with Limited Diet Variety:</strong> Low intake of fruits and vegetables (common with food insecurity, restrictive diets, or alcohol use disorder).</li>
                        <li><strong>People with Malabsorption Conditions:</strong> Certain GI disorders (Crohn's, celiac) or post-bariatric surgery.</li>
                        <li><strong>Individuals Under Severe Stress/Illness:</strong> Critical illness, major surgery, significant burns, or infections increase needs for tissue repair and fighting oxidative stress.</li>
                        <li><strong>People with End-Stage Renal Disease on Hemodialysis:</strong> May experience increased losses and have dietary restrictions.</li>
                    </ul>
                </div>

                <div className="content-block">
                    <h3>Symptoms of Deficiency (Scurvy)</h3>
                    <p>Severe deficiency leads to scurvy, though rare in developed countries. Early signs can be non-specific. Symptoms progress and may include:</p>
                    <ul>
                        <li>Fatigue, malaise, feeling weak.</li>
                        <li>Inflammation of the gums (gingivitis), bleeding gums, tooth loosening/loss.</li>
                        <li>Easy bruising (due to weak capillaries - impaired collagen).</li>
                        <li>Petechiae (small red/purple spots on skin), perifollicular hemorrhage (bleeding around hair follicles).</li>
                        <li>Poor wound healing.</li>
                        <li>Joint pain, potentially swelling.</li>
                        <li>Corkscrew hairs.</li>
                        <li>In later stages: depression, anemia (iron-deficiency type can worsen), bone abnormalities, potentially fatal complications.</li>
                    </ul>
                    <p>Symptoms typically appear when intake is below 10 mg/day for several weeks or months, depleting body stores.</p>
                </div>

                <div className="content-block">
                    <h3>Toxicity & Tolerable Upper Intake Level (UL)</h3>
                    <ul>
                        <li><strong>Low Toxicity:</strong> Vitamin C generally has low toxicity because excess is readily excreted in urine.</li>
                        <li><strong>UL:</strong> Set at 2000 mg (2 grams) per day for adults based on the prevention of osmotic diarrhea and GI disturbances. The UL applies to total intake from food and supplements.</li>
                        <li><strong>Common Side Effects of High Doses (&gt;UL):</strong> Primarily gastrointestinal issues like diarrhea, nausea, abdominal cramps, bloating. These are usually temporary and resolve when intake is lowered.</li>
                        <li><strong>Kidney Stones:</strong> High doses increase urinary oxalate and urate excretion. Theoretically, this could raise the risk of calcium oxalate and uric acid kidney stones, especially in predisposed individuals (e.g., those with a history of stones or kidney disease). However, evidence for a strong causal link in healthy people is inconsistent, but caution is advised.</li>
                        <li><strong>G6PD Deficiency:</strong> Caution advised with very high doses (especially intravenous), as rare cases of hemolysis (red blood cell breakdown) have been reported in individuals with this genetic condition.</li>
                        <li><strong>Iron Overload:</strong> Vitamin C enhances non-heme iron absorption. While generally beneficial, this could theoretically exacerbate iron overload in individuals with hereditary hemochromatosis.</li>
                        <li><strong>Lab Test Interference:</strong> Very high doses can interfere with certain lab tests (e.g., glucose tests, tests for occult blood in stool).</li>
                    </ul>
                    <p>Testing for Vitamin C status (plasma or leukocyte levels) is not routinely done but can be performed in specific clinical situations or research to assess deficiency or saturation.</p>
                </div>

                {/* --- Sources Section (Vitamin C specific) --- */}
                <div className="content-block">
                    <h3>Sources & Further Reading</h3>
                    <p className="sources" style={{ fontSize: '10px', lineHeight: '1.4' }}> {/* Inline style for example */}
                        National Institutes of Health, Office of Dietary Supplements. (n.d.). *Vitamin C Fact Sheet for Health Professionals*. Retrieved April 14, 2025, from https://ods.od.nih.gov/factsheets/VitaminC-HealthProfessional/ <br />
                        National Institutes of Health, Office of Dietary Supplements. (n.d.). *Vitamin C Fact Sheet for Consumers*. Retrieved April 14, 2025, from https://ods.od.nih.gov/factsheets/VitaminC-Consumer/ <br />
                        National Academies of Sciences, Engineering, and Medicine. (2000). *Dietary Reference Intakes for Vitamin C, Vitamin E, Selenium, and Carotenoids*. The National Academies Press. https://doi.org/10.17226/9810 <br />
                        Levine, M., Conry-Cantilena, C., Wang, Y., et al. (1996). Vitamin C pharmacokinetics in healthy volunteers: Evidence for a recommended dietary allowance. *Proceedings of the National Academy of Sciences*, *93*(8), 3704–3709. <br />
                        Padayatty, S. J., Sun, H., Wang, Y., et al. (2004). Vitamin C pharmacokinetics: implications for oral and intravenous use. *Annals of Internal Medicine*, *140*(7), 533–537. <br />
                        Massey, L. K., Liebman, M., & Kynast-Gales, S. A. (2005). Ascorbate increases human oxaluria and kidney stone risk. *Journal of Nutrition*, *135*(7), 1673–1677. <br />
                        USDA FoodData Central. (n.d.). Retrieved April 14, 2025, from https://fdc.nal.usda.gov/
                    </p>
                </div>
                {/* --- END OF SOURCES SECTION --- */}
            </div> {/* End educational-section */}

        </div> // End vitaminCCalculator
    );
}

export default VitaminCCalculator;